import React, { useState, useMemo, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ArrowLeft, 
  Search, 
  User, 
  Calendar, 
  MapPin, 
  Filter, 
  Download, 
  TrendingUp, 
  DollarSign, 
  Users, 
  ShoppingBag,
  SortAsc,
  SortDesc,
  RefreshCw,
  BarChart3,
  PieChart,
  Eye,
  FileText,
  Clock,
  CreditCard,
  Receipt,
  Star,
  ChevronDown,
  ChevronUp,
  History
} from 'lucide-react';
import BottomNavigation from '../../components/common/BottomNavigation';
import { useApp } from '../../context/AppContext';

interface BillingRecord {
  id: string;
  customerId: string;
  customerName: string;
  tableId?: string;
  tableNumber?: string;
  tableLocation?: string;
  totalAmount: number;
  paymentMethod: string;
  transactionId?: string | null;
  orderIds: string[];
  numberOfOrders: number;
  totalItems: number;
  paidAt: string;
  sessionStartTime?: string;
  isTestRecord?: boolean;
}

interface CustomerSummary {
  customerId: string;
  customerName: string;
  totalVisits: number;
  totalSpent: number;
  totalOrders: number;
  totalItems: number;
  averageSpentPerVisit: number;
  averageOrdersPerVisit: number;
  firstVisit: string;
  lastVisit: string;
  favoritePaymentMethod: string;
  billingRecords: BillingRecord[];
  paymentMethods: Set<string>;
  visitFrequency: number; // visits per month
  customerValue: 'high' | 'medium' | 'low';
}

const CustomerBillingHistory: React.FC = () => {
  const { setCurrentScreen } = useApp();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCustomer, setSelectedCustomer] = useState<CustomerSummary | null>(null);
  const [showCustomerDetails, setShowCustomerDetails] = useState(false);
  const [billingHistory, setBillingHistory] = useState<BillingRecord[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [expandedCustomers, setExpandedCustomers] = useState<Set<string>>(new Set());
  
  // Enhanced filtering and sorting states
  const [showFilters, setShowFilters] = useState(false);
  const [dateRange, setDateRange] = useState({ start: '', end: '' });
  const [paymentMethodFilter, setPaymentMethodFilter] = useState<string>('all');
  const [customerValueFilter, setCustomerValueFilter] = useState<string>('all');
  const [sortBy, setSortBy] = useState<'totalSpent' | 'visits' | 'lastVisit' | 'name'>('totalSpent');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [showAnalytics, setShowAnalytics] = useState(false);
  const [hideTestRecords, setHideTestRecords] = useState(true);
  const [minSpentFilter, setMinSpentFilter] = useState<number>(0);
  const [minVisitsFilter, setMinVisitsFilter] = useState<number>(0);

  const handleBack = () => {
    setCurrentScreen('admin-dashboard');
  };

  // Load billing history from Firestore
  useEffect(() => {
    const loadBillingHistory = async () => {
      try {
        console.log('🔄 CustomerBillingHistory: Loading billing data...');
        setIsLoading(true);
        
        const { FirestoreService } = await import('../../firebase/firestore');
        const history = await FirestoreService.getCustomerBillingHistory();
        
        console.log('📊 CustomerBillingHistory: Loaded', history.length, 'billing records');
        setBillingHistory(history);
        
      } catch (error) {
        console.error('❌ CustomerBillingHistory: Error loading billing history:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadBillingHistory();

    // Auto-refresh every 30 seconds
    const interval = setInterval(loadBillingHistory, 30000);
    return () => clearInterval(interval);
  }, []);

  // Process billing data into customer summaries
  const customerSummaries = useMemo(() => {
    console.log('🔄 Processing billing data into customer summaries...');
    
    // Filter out test records if needed
    let filteredHistory = billingHistory;
    if (hideTestRecords) {
      filteredHistory = billingHistory.filter(record => {
        const isTestRecord = 
          record.customerId?.startsWith('TEST_CUSTOMER_') ||
          record.customerId?.startsWith('DIAGNOSTIC_TEST_') ||
          record.customerName?.includes('Test Customer') ||
          record.customerName?.includes('Diagnostic Test Customer');
        return !isTestRecord;
      });
    }

    // Group by customer
    const customerGroups = filteredHistory.reduce((acc, record) => {
      const customerId = record.customerId;
      
      if (!acc[customerId]) {
        acc[customerId] = {
          customerId,
          customerName: record.customerName,
          billingRecords: [],
          paymentMethods: new Set<string>()
        };
      }
      
      acc[customerId].billingRecords!.push(record);
      acc[customerId].paymentMethods!.add(record.paymentMethod);
      
      return acc;
    }, {} as Record<string, Partial<CustomerSummary>>);

    // Calculate customer summaries
    const summaries: CustomerSummary[] = Object.values(customerGroups).map(group => {
      const records = group.billingRecords!;
      const totalSpent = records.reduce((sum, r) => sum + r.totalAmount, 0);
      const totalOrders = records.reduce((sum, r) => sum + r.numberOfOrders, 0);
      const totalItems = records.reduce((sum, r) => sum + r.totalItems, 0);
      
      // Sort records by date to find first and last visit
      const sortedRecords = records.sort((a, b) => new Date(a.paidAt).getTime() - new Date(b.paidAt).getTime());
      const firstVisit = sortedRecords[0].paidAt;
      const lastVisit = sortedRecords[sortedRecords.length - 1].paidAt;
      
      // Calculate visit frequency (visits per month)
      const daysBetween = (new Date(lastVisit).getTime() - new Date(firstVisit).getTime()) / (1000 * 60 * 60 * 24);
      const monthsBetween = Math.max(daysBetween / 30, 1); // At least 1 month
      const visitFrequency = records.length / monthsBetween;
      
      // Find favorite payment method
      const paymentMethodCounts = records.reduce((acc, r) => {
        acc[r.paymentMethod] = (acc[r.paymentMethod] || 0) + 1;
        return acc;
      }, {} as Record<string, number>);
      const favoritePaymentMethod = Object.entries(paymentMethodCounts)
        .sort(([,a], [,b]) => b - a)[0]?.[0] || 'unknown';
      
      // Determine customer value
      let customerValue: 'high' | 'medium' | 'low' = 'low';
      if (totalSpent >= 500 || records.length >= 10) {
        customerValue = 'high';
      } else if (totalSpent >= 200 || records.length >= 5) {
        customerValue = 'medium';
      }

      return {
        customerId: group.customerId!,
        customerName: group.customerName!,
        totalVisits: records.length,
        totalSpent,
        totalOrders,
        totalItems,
        averageSpentPerVisit: totalSpent / records.length,
        averageOrdersPerVisit: totalOrders / records.length,
        firstVisit,
        lastVisit,
        favoritePaymentMethod,
        billingRecords: records.sort((a, b) => new Date(b.paidAt).getTime() - new Date(a.paidAt).getTime()),
        paymentMethods: group.paymentMethods!,
        visitFrequency,
        customerValue
      };
    });

    console.log('✅ Processed', summaries.length, 'customer summaries');
    return summaries;
  }, [billingHistory, hideTestRecords]);

  // Filter and sort customer summaries
  const filteredAndSortedCustomers = useMemo(() => {
    let filtered = customerSummaries.filter(customer => {
      // Search filter
      const matchesSearch = customer.customerName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        customer.customerId.toLowerCase().includes(searchTerm.toLowerCase());
      
      // Date range filter (based on last visit)
      const lastVisitDate = new Date(customer.lastVisit);
      const matchesDateRange = (!dateRange.start || lastVisitDate >= new Date(dateRange.start)) &&
        (!dateRange.end || lastVisitDate <= new Date(dateRange.end + 'T23:59:59'));
      
      // Payment method filter
      const matchesPaymentMethod = paymentMethodFilter === 'all' || 
        Array.from(customer.paymentMethods).includes(paymentMethodFilter);
      
      // Customer value filter
      const matchesCustomerValue = customerValueFilter === 'all' || 
        customer.customerValue === customerValueFilter;
      
      // Minimum spent filter
      const matchesMinSpent = customer.totalSpent >= minSpentFilter;
      
      // Minimum visits filter
      const matchesMinVisits = customer.totalVisits >= minVisitsFilter;
      
      return matchesSearch && matchesDateRange && matchesPaymentMethod && 
             matchesCustomerValue && matchesMinSpent && matchesMinVisits;
    });

    // Sort the filtered results
    filtered.sort((a, b) => {
      let comparison = 0;
      
      switch (sortBy) {
        case 'totalSpent':
          comparison = a.totalSpent - b.totalSpent;
          break;
        case 'visits':
          comparison = a.totalVisits - b.totalVisits;
          break;
        case 'lastVisit':
          comparison = new Date(a.lastVisit).getTime() - new Date(b.lastVisit).getTime();
          break;
        case 'name':
          comparison = a.customerName.localeCompare(b.customerName);
          break;
        default:
          comparison = 0;
      }
      
      return sortOrder === 'asc' ? comparison : -comparison;
    });

    return filtered;
  }, [customerSummaries, searchTerm, dateRange, paymentMethodFilter, customerValueFilter, 
      sortBy, sortOrder, minSpentFilter, minVisitsFilter]);

  // Analytics calculations
  const analytics = useMemo(() => {
    const totalCustomers = filteredAndSortedCustomers.length;
    const totalRevenue = filteredAndSortedCustomers.reduce((sum, c) => sum + c.totalSpent, 0);
    const totalVisits = filteredAndSortedCustomers.reduce((sum, c) => sum + c.totalVisits, 0);
    const totalOrders = filteredAndSortedCustomers.reduce((sum, c) => sum + c.totalOrders, 0);
    
    const averageSpentPerCustomer = totalCustomers > 0 ? totalRevenue / totalCustomers : 0;
    const averageVisitsPerCustomer = totalCustomers > 0 ? totalVisits / totalCustomers : 0;
    const averageOrdersPerCustomer = totalCustomers > 0 ? totalOrders / totalCustomers : 0;
    
    // Customer value distribution
    const customerValueDistribution = filteredAndSortedCustomers.reduce((acc, c) => {
      acc[c.customerValue] = (acc[c.customerValue] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);
    
    // Top customers by spending
    const topCustomers = [...filteredAndSortedCustomers]
      .sort((a, b) => b.totalSpent - a.totalSpent)
      .slice(0, 5);
    
    // Payment method preferences
    const paymentMethodPreferences = filteredAndSortedCustomers.reduce((acc, c) => {
      acc[c.favoritePaymentMethod] = (acc[c.favoritePaymentMethod] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    return {
      totalCustomers,
      totalRevenue,
      totalVisits,
      totalOrders,
      averageSpentPerCustomer,
      averageVisitsPerCustomer,
      averageOrdersPerCustomer,
      customerValueDistribution,
      topCustomers,
      paymentMethodPreferences
    };
  }, [filteredAndSortedCustomers]);

  const getCustomerValueColor = (value: string) => {
    switch (value) {
      case 'high':
        return 'bg-green-900 text-green-400 border-green-500';
      case 'medium':
        return 'bg-yellow-900 text-yellow-400 border-yellow-500';
      case 'low':
        return 'bg-gray-900 text-gray-400 border-gray-500';
      default:
        return 'bg-gray-900 text-gray-400 border-gray-500';
    }
  };

  const getPaymentMethodColor = (method: string) => {
    switch (method) {
      case 'cash':
        return 'bg-green-900 text-green-400';
      case 'card':
        return 'bg-blue-900 text-blue-400';
      case 'upi':
        return 'bg-purple-900 text-purple-400';
      default:
        return 'bg-gray-900 text-gray-400';
    }
  };

  const getPaymentMethodText = (method: string) => {
    switch (method) {
      case 'cash':
        return '💵 Cash';
      case 'card':
        return '💳 Card';
      case 'upi':
        return '📱 UPI';
      default:
        return method;
    }
  };

  const toggleCustomerExpansion = (customerId: string) => {
    const newExpanded = new Set(expandedCustomers);
    if (newExpanded.has(customerId)) {
      newExpanded.delete(customerId);
    } else {
      newExpanded.add(customerId);
    }
    setExpandedCustomers(newExpanded);
  };

  const exportToCSV = () => {
    const headers = [
      'Customer Name',
      'Customer ID',
      'Total Visits',
      'Total Spent',
      'Total Orders',
      'Total Items',
      'Average Spent Per Visit',
      'Average Orders Per Visit',
      'First Visit',
      'Last Visit',
      'Favorite Payment Method',
      'Customer Value',
      'Visit Frequency (per month)'
    ];

    const csvData = filteredAndSortedCustomers.map(customer => [
      customer.customerName,
      customer.customerId,
      customer.totalVisits,
      customer.totalSpent.toFixed(2),
      customer.totalOrders,
      customer.totalItems,
      customer.averageSpentPerVisit.toFixed(2),
      customer.averageOrdersPerVisit.toFixed(1),
      new Date(customer.firstVisit).toLocaleDateString(),
      new Date(customer.lastVisit).toLocaleDateString(),
      customer.favoritePaymentMethod,
      customer.customerValue,
      customer.visitFrequency.toFixed(1)
    ]);

    const csvContent = [headers, ...csvData]
      .map(row => row.map(cell => `"${cell}"`).join(','))
      .join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `customer-billing-history-${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const clearFilters = () => {
    setSearchTerm('');
    setDateRange({ start: '', end: '' });
    setPaymentMethodFilter('all');
    setCustomerValueFilter('all');
    setSortBy('totalSpent');
    setSortOrder('desc');
    setMinSpentFilter(0);
    setMinVisitsFilter(0);
  };

  return (
    <div className="min-h-screen bg-slate-900 pb-20">
      {/* Header */}
      <div className="bg-slate-800 border-b border-slate-700">
        <div className="flex items-center justify-between p-6">
          <div className="flex items-center">
            <button
              onClick={handleBack}
              className="p-2 text-slate-400 hover:text-white mr-4"
            >
              <ArrowLeft size={20} />
            </button>
            <div>
              <h1 className="text-xl font-bold text-white">Customer Billing History</h1>
              <p className="text-slate-400 text-sm">Customer-wise billing analysis and history</p>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            <button
              onClick={() => setShowAnalytics(!showAnalytics)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                showAnalytics 
                  ? 'bg-teal-600 text-white' 
                  : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
              }`}
            >
              <BarChart3 size={16} className="inline mr-2" />
              Analytics
            </button>
            
            <button
              onClick={exportToCSV}
              className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white text-sm rounded-lg transition-colors"
            >
              <Download size={16} className="inline mr-2" />
              Export CSV
            </button>
          </div>
        </div>

        {/* Analytics Panel */}
        <AnimatePresence>
          {showAnalytics && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              className="border-t border-slate-700 bg-slate-750"
            >
              <div className="p-6">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                  <div className="bg-gradient-to-r from-green-900 to-emerald-900 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <DollarSign className="text-green-400" size={24} />
                      <span className="text-green-400 text-xs font-medium">REVENUE</span>
                    </div>
                    <div className="text-white text-2xl font-bold">${analytics.totalRevenue.toFixed(2)}</div>
                    <div className="text-green-300 text-sm">Avg: ${analytics.averageSpentPerCustomer.toFixed(2)}/customer</div>
                  </div>

                  <div className="bg-gradient-to-r from-blue-900 to-indigo-900 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <Users className="text-blue-400" size={24} />
                      <span className="text-blue-400 text-xs font-medium">CUSTOMERS</span>
                    </div>
                    <div className="text-white text-2xl font-bold">{analytics.totalCustomers}</div>
                    <div className="text-blue-300 text-sm">Avg: {analytics.averageVisitsPerCustomer.toFixed(1)} visits/customer</div>
                  </div>

                  <div className="bg-gradient-to-r from-purple-900 to-pink-900 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <ShoppingBag className="text-purple-400" size={24} />
                      <span className="text-purple-400 text-xs font-medium">ORDERS</span>
                    </div>
                    <div className="text-white text-2xl font-bold">{analytics.totalOrders}</div>
                    <div className="text-purple-300 text-sm">Avg: {analytics.averageOrdersPerCustomer.toFixed(1)}/customer</div>
                  </div>

                  <div className="bg-gradient-to-r from-orange-900 to-red-900 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <TrendingUp className="text-orange-400" size={24} />
                      <span className="text-orange-400 text-xs font-medium">VISITS</span>
                    </div>
                    <div className="text-white text-2xl font-bold">{analytics.totalVisits}</div>
                    <div className="text-orange-300 text-sm">Total customer visits</div>
                  </div>
                </div>

                {/* Customer Value Distribution & Top Customers */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="bg-slate-800 rounded-lg p-4">
                    <h3 className="text-white font-semibold mb-3 flex items-center">
                      <PieChart size={18} className="mr-2" />
                      Customer Value Distribution
                    </h3>
                    <div className="space-y-2">
                      {Object.entries(analytics.customerValueDistribution).map(([value, count]) => (
                        <div key={value} className="flex items-center justify-between">
                          <span className={`px-2 py-1 rounded text-xs font-medium ${getCustomerValueColor(value)}`}>
                            {value.toUpperCase()} VALUE
                          </span>
                          <div className="flex items-center gap-2">
                            <div className="w-20 bg-slate-700 rounded-full h-2">
                              <div 
                                className={`h-2 rounded-full ${getCustomerValueColor(value).replace('text-', 'bg-').replace('900', '500')}`}
                                style={{ width: `${(count / analytics.totalCustomers) * 100}%` }}
                              />
                            </div>
                            <span className="text-white font-medium text-sm w-8">{count}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="bg-slate-800 rounded-lg p-4">
                    <h3 className="text-white font-semibold mb-3 flex items-center">
                      <Star size={18} className="mr-2" />
                      Top 5 Customers by Spending
                    </h3>
                    <div className="space-y-2">
                      {analytics.topCustomers.map((customer, index) => (
                        <div key={customer.customerId} className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <span className="text-slate-400 text-sm w-4">#{index + 1}</span>
                            <span className="text-slate-300 text-sm truncate">{customer.customerName}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-white font-medium text-sm">${customer.totalSpent.toFixed(0)}</span>
                            <span className="text-slate-400 text-xs">({customer.totalVisits} visits)</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      <div className="p-6 space-y-6">
        {/* Search and Filter Bar */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-4"
        >
          <div className="flex flex-col md:flex-row gap-4">
            {/* Search Input */}
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" size={20} />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-3 bg-slate-800 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-teal-500"
                placeholder="Search by customer name or ID..."
              />
            </div>

            {/* Filter Toggle */}
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`px-4 py-3 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 ${
                showFilters || dateRange.start || dateRange.end || paymentMethodFilter !== 'all' || customerValueFilter !== 'all' || minSpentFilter > 0 || minVisitsFilter > 0
                  ? 'bg-teal-600 text-white' 
                  : 'bg-slate-800 text-slate-300 hover:bg-slate-700 border border-slate-600'
              }`}
            >
              <Filter size={16} />
              Filters
              {(dateRange.start || dateRange.end || paymentMethodFilter !== 'all' || customerValueFilter !== 'all' || minSpentFilter > 0 || minVisitsFilter > 0) && (
                <span className="bg-white bg-opacity-20 rounded-full px-2 py-1 text-xs">
                  {[
                    dateRange.start && 'Date', 
                    paymentMethodFilter !== 'all' && 'Payment',
                    customerValueFilter !== 'all' && 'Value',
                    minSpentFilter > 0 && 'Spent',
                    minVisitsFilter > 0 && 'Visits'
                  ].filter(Boolean).length}
                </span>
              )}
            </button>

            <button
              onClick={() => setHideTestRecords(!hideTestRecords)}
              className={`px-4 py-3 text-white text-sm rounded-lg transition-colors ${
                hideTestRecords 
                  ? 'bg-purple-600 hover:bg-purple-700' 
                  : 'bg-gray-600 hover:bg-gray-700'
              }`}
            >
              {hideTestRecords ? '👁️ Show Test' : '🙈 Hide Test'}
            </button>
          </div>

          {/* Advanced Filters */}
          <AnimatePresence>
            {showFilters && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                className="bg-slate-800 border border-slate-600 rounded-lg p-4"
              >
                <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4">
                  {/* Date Range */}
                  <div>
                    <label className="block text-slate-300 text-sm font-medium mb-2">Start Date</label>
                    <input
                      type="date"
                      value={dateRange.start}
                      onChange={(e) => setDateRange(prev => ({ ...prev, start: e.target.value }))}
                      className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded text-white text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                    />
                  </div>

                  <div>
                    <label className="block text-slate-300 text-sm font-medium mb-2">End Date</label>
                    <input
                      type="date"
                      value={dateRange.end}
                      onChange={(e) => setDateRange(prev => ({ ...prev, end: e.target.value }))}
                      className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded text-white text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                    />
                  </div>

                  {/* Payment Method Filter */}
                  <div>
                    <label className="block text-slate-300 text-sm font-medium mb-2">Payment Method</label>
                    <select
                      value={paymentMethodFilter}
                      onChange={(e) => setPaymentMethodFilter(e.target.value)}
                      className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded text-white text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                    >
                      <option value="all">All Methods</option>
                      <option value="cash">💵 Cash</option>
                      <option value="card">💳 Card</option>
                      <option value="upi">📱 UPI</option>
                    </select>
                  </div>

                  {/* Customer Value Filter */}
                  <div>
                    <label className="block text-slate-300 text-sm font-medium mb-2">Customer Value</label>
                    <select
                      value={customerValueFilter}
                      onChange={(e) => setCustomerValueFilter(e.target.value)}
                      className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded text-white text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                    >
                      <option value="all">All Values</option>
                      <option value="high">High Value</option>
                      <option value="medium">Medium Value</option>
                      <option value="low">Low Value</option>
                    </select>
                  </div>

                  {/* Minimum Spent Filter */}
                  <div>
                    <label className="block text-slate-300 text-sm font-medium mb-2">Min Spent ($)</label>
                    <input
                      type="number"
                      value={minSpentFilter}
                      onChange={(e) => setMinSpentFilter(Number(e.target.value))}
                      className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded text-white text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                      min="0"
                      step="10"
                    />
                  </div>

                  {/* Minimum Visits Filter */}
                  <div>
                    <label className="block text-slate-300 text-sm font-medium mb-2">Min Visits</label>
                    <input
                      type="number"
                      value={minVisitsFilter}
                      onChange={(e) => setMinVisitsFilter(Number(e.target.value))}
                      className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded text-white text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                      min="0"
                      step="1"
                    />
                  </div>
                </div>

                {/* Sort Options */}
                <div className="flex items-center justify-between mt-4 pt-4 border-t border-slate-600">
                  <div className="flex items-center gap-4">
                    <div>
                      <label className="block text-slate-300 text-sm font-medium mb-2">Sort By</label>
                      <div className="flex gap-2">
                        <select
                          value={sortBy}
                          onChange={(e) => setSortBy(e.target.value as any)}
                          className="px-3 py-2 bg-slate-700 border border-slate-600 rounded text-white text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                        >
                          <option value="totalSpent">Total Spent</option>
                          <option value="visits">Total Visits</option>
                          <option value="lastVisit">Last Visit</option>
                          <option value="name">Customer Name</option>
                        </select>
                        <button
                          onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
                          className="px-3 py-2 bg-slate-700 border border-slate-600 rounded text-slate-300 hover:text-white transition-colors"
                        >
                          {sortOrder === 'asc' ? <SortAsc size={16} /> : <SortDesc size={16} />}
                        </button>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-4">
                    <span className="text-slate-400 text-sm">
                      Showing {filteredAndSortedCustomers.length} of {customerSummaries.length} customers
                    </span>
                    <button
                      onClick={clearFilters}
                      className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-slate-300 text-sm rounded transition-colors"
                    >
                      Clear Filters
                    </button>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>

        {/* Customer Summary Stats */}
        {filteredAndSortedCustomers.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-slate-800 rounded-lg p-4 mb-6"
          >
            <h3 className="text-white font-semibold text-lg mb-3">👥 Customer Overview</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="text-center">
                <div className="text-2xl font-bold text-blue-400">{analytics.totalCustomers}</div>
                <div className="text-slate-400 text-sm">Total Customers</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-green-400">${analytics.totalRevenue.toFixed(0)}</div>
                <div className="text-slate-400 text-sm">Total Revenue</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-purple-400">
                  ${analytics.averageSpentPerCustomer.toFixed(0)}
                </div>
                <div className="text-slate-400 text-sm">Avg per Customer</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-orange-400">
                  {analytics.averageVisitsPerCustomer.toFixed(1)}
                </div>
                <div className="text-slate-400 text-sm">Avg Visits/Customer</div>
              </div>
            </div>
          </motion.div>
        )}

        {/* Customer List */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-lg font-semibold text-white">👥 Customer Billing History</h2>
              <p className="text-slate-400 text-sm">
                {filteredAndSortedCustomers.length} of {customerSummaries.length} customers
                {searchTerm && ` matching "${searchTerm}"`}
              </p>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={async () => {
                  setIsLoading(true);
                  const { FirestoreService } = await import('../../firebase/firestore');
                  const history = await FirestoreService.getCustomerBillingHistory();
                  setBillingHistory(history);
                  setIsLoading(false);
                }}
                className="px-3 py-2 bg-teal-600 hover:bg-teal-700 text-white text-sm rounded-lg transition-colors flex items-center gap-2"
                disabled={isLoading}
              >
                <RefreshCw size={16} className={isLoading ? 'animate-spin' : ''} />
                Refresh
              </button>
            </div>
          </div>
          
          {isLoading ? (
            <div className="text-center py-12 text-slate-400">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-500 mx-auto mb-4"></div>
              Loading customer billing history...
            </div>
          ) : (
            <div className="space-y-4">
              {filteredAndSortedCustomers.map((customer, index) => (
                <motion.div
                  key={customer.customerId}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-slate-800 rounded-lg border border-slate-700 overflow-hidden"
                >
                  {/* Customer Header */}
                  <div className="bg-gradient-to-r from-purple-600 to-teal-600 p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <div className="w-12 h-12 bg-white bg-opacity-20 rounded-full flex items-center justify-center">
                          <User className="text-white" size={20} />
                        </div>
                        <div>
                          <h3 className="text-white font-bold text-xl">{customer.customerName}</h3>
                          <p className="text-white text-sm opacity-90">ID: {customer.customerId}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className={`px-3 py-1 rounded-full text-xs font-medium border ${getCustomerValueColor(customer.customerValue)}`}>
                          {customer.customerValue.toUpperCase()} VALUE
                        </span>
                        <div className="text-right">
                          <div className="text-white font-bold text-2xl">${customer.totalSpent.toFixed(2)}</div>
                          <div className="text-white text-sm opacity-90">{customer.totalVisits} visits</div>
                        </div>
                      </div>
                    </div>
                    
                    {/* Customer Statistics */}
                    <div className="grid grid-cols-5 gap-4 mt-4">
                      <div className="bg-white bg-opacity-10 rounded-lg p-3 text-center">
                        <div className="text-white text-lg font-bold">{customer.totalVisits}</div>
                        <div className="text-white text-xs opacity-75">Visits</div>
                      </div>
                      <div className="bg-white bg-opacity-10 rounded-lg p-3 text-center">
                        <div className="text-white text-lg font-bold">{customer.totalOrders}</div>
                        <div className="text-white text-xs opacity-75">Orders</div>
                      </div>
                      <div className="bg-white bg-opacity-10 rounded-lg p-3 text-center">
                        <div className="text-white text-lg font-bold">{customer.totalItems}</div>
                        <div className="text-white text-xs opacity-75">Items</div>
                      </div>
                      <div className="bg-white bg-opacity-10 rounded-lg p-3 text-center">
                        <div className="text-white text-lg font-bold">
                          ${customer.averageSpentPerVisit.toFixed(0)}
                        </div>
                        <div className="text-white text-xs opacity-75">Avg/Visit</div>
                      </div>
                      <div className="bg-white bg-opacity-10 rounded-lg p-3 text-center">
                        <div className="text-white text-lg font-bold">
                          {customer.visitFrequency.toFixed(1)}
                        </div>
                        <div className="text-white text-xs opacity-75">Visits/Month</div>
                      </div>
                    </div>
                    
                    {/* Customer Details */}
                    <div className="mt-3 grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="text-white opacity-75">First Visit:</span>
                        <span className="text-white ml-2">{new Date(customer.firstVisit).toLocaleDateString()}</span>
                      </div>
                      <div>
                        <span className="text-white opacity-75">Last Visit:</span>
                        <span className="text-white ml-2">{new Date(customer.lastVisit).toLocaleDateString()}</span>
                      </div>
                    </div>
                    
                    {/* Payment Methods Used */}
                    <div className="mt-3 flex items-center gap-2">
                      <span className="text-white text-sm opacity-75">Payment Methods:</span>
                      {Array.from(customer.paymentMethods).map((method) => (
                        <span key={method} className={`px-2 py-1 rounded-full text-xs font-medium ${getPaymentMethodColor(method)}`}>
                          {getPaymentMethodText(method)}
                        </span>
                      ))}
                      <span className="text-white text-sm opacity-75 ml-2">
                        (Prefers: {getPaymentMethodText(customer.favoritePaymentMethod)})
                      </span>
                    </div>
                    
                    {/* Expand/Collapse Button */}
                    <div className="mt-4 flex justify-center">
                      <button
                        onClick={() => toggleCustomerExpansion(customer.customerId)}
                        className="flex items-center gap-2 px-4 py-2 bg-white bg-opacity-10 rounded-lg text-white text-sm hover:bg-opacity-20 transition-colors"
                      >
                        <History size={16} />
                        {expandedCustomers.has(customer.customerId) ? 'Hide' : 'Show'} Billing History
                        {expandedCustomers.has(customer.customerId) ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                      </button>
                    </div>
                  </div>
                  
                  {/* Customer's Billing Records */}
                  <AnimatePresence>
                    {expandedCustomers.has(customer.customerId) && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        className="p-4 bg-slate-750"
                      >
                        <h4 className="text-white font-semibold mb-3 flex items-center">
                          📋 Complete Billing History ({customer.billingRecords.length} records)
                        </h4>
                        <div className="space-y-2 max-h-96 overflow-y-auto">
                          {customer.billingRecords.map((record, recordIndex) => (
                            <motion.div
                              key={record.id}
                              initial={{ opacity: 0, x: -10 }}
                              animate={{ opacity: 1, x: 0 }}
                              transition={{ delay: recordIndex * 0.05 }}
                              className="p-3 bg-slate-700 rounded-lg hover:bg-slate-600 transition-colors"
                            >
                              <div className="flex items-center justify-between">
                                <div className="flex items-center space-x-4">
                                  <div className="flex items-center space-x-2 text-sm">
                                    <Calendar size={14} className="text-slate-400" />
                                    <span className="text-slate-300">{new Date(record.paidAt).toLocaleDateString()}</span>
                                    <span className="text-slate-500">{new Date(record.paidAt).toLocaleTimeString()}</span>
                                  </div>
                                  <div className="flex items-center space-x-2 text-sm">
                                    <MapPin size={14} className="text-slate-400" />
                                    <span className="text-slate-300">{record.tableNumber || 'N/A'}</span>
                                  </div>
                                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getPaymentMethodColor(record.paymentMethod)}`}>
                                    {getPaymentMethodText(record.paymentMethod)}
                                  </span>
                                </div>
                                <div className="flex items-center space-x-4">
                                  <div className="text-right text-sm">
                                    <div className="text-slate-400">
                                      {record.numberOfOrders} orders • {record.totalItems} items
                                    </div>
                                    {record.transactionId && (
                                      <div className="text-slate-500 text-xs">
                                        TXN: {record.transactionId}
                                      </div>
                                    )}
                                  </div>
                                  <div className="text-green-400 font-bold text-lg">${record.totalAmount.toFixed(2)}</div>
                                </div>
                              </div>
                            </motion.div>
                          ))}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
              ))}
              
              {filteredAndSortedCustomers.length === 0 && !isLoading && (
                <div className="text-center py-12">
                  <div className="text-6xl mb-4">👥</div>
                  <h3 className="text-xl font-semibold text-white mb-2">
                    {searchTerm ? 'No Customers Found' : 'No Customer Data Yet'}
                  </h3>
                  <p className="text-slate-400 mb-4">
                    {searchTerm 
                      ? 'No customers found matching your search criteria.' 
                      : 'Customer billing data will appear here when payments are completed.'}
                  </p>
                  {!searchTerm && (
                    <div className="bg-slate-700 rounded-lg p-4 max-w-md mx-auto text-left">
                      <p className="text-slate-300 text-sm mb-2">To create customer billing records:</p>
                      <ol className="text-slate-400 text-sm space-y-1 list-decimal list-inside">
                        <li>Login as a customer</li>
                        <li>Select a table</li>
                        <li>Place orders</li>
                        <li>Go to "Pay Bill & Exit"</li>
                        <li>Complete the payment</li>
                      </ol>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <BottomNavigation />
    </div>
  );
};

export default CustomerBillingHistory;